import React, { useCallback, useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useAppStore } from "@stores/useStores";

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Popup component using observer HOC for optimal performance
 * Note: observer already applies React.memo internally
 */
const Popup = observer(function Popup(): React.ReactElement {
    const appStore = useAppStore();
    const dialogRef = useRef<HTMLDivElement>(null);
    const isPopupDisplayed = appStore.isPopUpDisplayed;

    const handleClose = useCallback((): void => {
        appStore.hidePopup();
    }, [appStore]);

    const handleBackdropClick = useCallback((e: React.MouseEvent): void => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    }, [handleClose]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent): void => {
        if (e.key === 'Escape') {
            handleClose();
            return;
        }

        // Focus trap: cycle focus within the dialog
        if (e.key === 'Tab' && dialogRef.current) {
            const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement.focus();
                }
            } else {
                if (document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement.focus();
                }
            }
        }
    }, [handleClose]);

    // Restore focus to the previously focused element when closing
    useEffect(() => {
        if (!isPopupDisplayed) return;

        const previouslyFocused = document.activeElement as HTMLElement | null;

        return () => {
            previouslyFocused?.focus();
        };
    }, [isPopupDisplayed]);

    if (!isPopupDisplayed) {
        return <></>;
    }

    return (
        <div
            className="popup-overlay fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={handleBackdropClick}
            onKeyDown={handleKeyDown}
            role="dialog"
            aria-modal="true"
            aria-labelledby="popup-title"
        >
            <div ref={dialogRef} className="popup bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl dark:bg-gray-800">
                <h2 id="popup-title" className="text-xl font-semibold mb-4 dark:text-white">Popup</h2>
                <p className="text-gray-600 mb-6 dark:text-gray-300">
                    This is a sample popup component with accessibility support.
                </p>
                <button
                    onClick={handleClose}
                    className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-2 dark:bg-gray-700 dark:hover:bg-gray-600"
                    autoFocus
                >
                    Close
                </button>
            </div>
        </div>
    );
});

export default Popup;
